import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Pencil, Upload, FileText } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loading } from "@/components/pawkt/Empty";

const KIND = { faq: "FAQ", policy: "Policy", document: "Document" };
const BLANK = { id: "", kind: "faq", title: "", body: "", branch_id: "" };

function BranchSelect({ value, onChange, branches, testid }) {
  return (
    <select className="h-9 rounded-md border border-slate-200 px-2 text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">All branches</option>
      {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
    </select>);
}

function Editor({ f, set, branches, onSave, onCancel, busy }) {
  const doc = f.kind === "document";
  return (
    <div className="space-y-2 rounded-2xl border border-violet-200 bg-violet-50/40 p-4" data-testid="kb-editor">
      <div className="flex flex-wrap gap-2">
        {!f.id && <select className="h-9 rounded-md border border-slate-200 px-2 text-sm" value={f.kind} onChange={(e) => set({ ...f, kind: e.target.value })} data-testid="kb-kind">
          <option value="faq">FAQ</option><option value="policy">Policy</option></select>}
        <BranchSelect value={f.branch_id} onChange={(v) => set({ ...f, branch_id: v })} branches={branches} testid="kb-branch" />
      </div>
      <Input placeholder={f.kind === "faq" ? "Question, e.g. Do you groom cats?" : "Title, e.g. Cancellation policy"} value={f.title} onChange={(e) => set({ ...f, title: e.target.value })} data-testid="kb-title" />
      {!doc && <Textarea rows={4} placeholder="Answer or policy text" value={f.body} onChange={(e) => set({ ...f, body: e.target.value })} data-testid="kb-body" />}
      <div className="flex gap-2">
        <Button size="sm" disabled={busy || !f.title.trim() || (!doc && !f.body.trim())} onClick={onSave} data-testid="kb-save">Save</Button>
        <Button size="sm" variant="outline" onClick={onCancel} data-testid="kb-cancel">Cancel</Button>
      </div>
    </div>);
}

function Row({ it, branches, onEdit, onDelete }) {
  const br = branches.find((b) => b.id === it.branch_id)?.name || "All branches";
  return (
    <li className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`kb-item-${it.id}`}>
      <div className="min-w-0 text-sm">
        <p className="font-semibold">{it.kind === "document" && <FileText className="mr-1 inline h-4 w-4 text-slate-500" />}{it.title}</p>
        <p className="line-clamp-2 text-slate-600">{it.kind === "document" ? `${it.file_name} · ${it.chars.toLocaleString("en-IN")} characters of text` : it.body}</p>
        <p className="mt-1 text-xs text-slate-500">{KIND[it.kind]} · {br}</p>
      </div>
      <div className="flex shrink-0 gap-1">
        <Button size="icon" variant="ghost" onClick={() => onEdit(it)} data-testid={`kb-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => onDelete(it)} data-testid={`kb-delete-${it.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
      </div>
    </li>);
}

function UploadBox({ branches, onDone }) {
  const [br, setBr] = useState("");
  const [busy, setBusy] = useState(false);
  const up = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("branch_id", br);
    setBusy(true);
    try { await api.post("/receptionist/knowledge/upload", fd); toast.success("Document added to the receptionist's knowledge"); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="kb-upload-box">
      <BranchSelect value={br} onChange={setBr} branches={branches} testid="kb-upload-branch" />
      <label className={`inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium ${busy ? "opacity-50" : ""}`}>
        <Upload className="h-4 w-4" />{busy ? "Reading…" : "Upload PDF or text"}
        <input type="file" accept=".pdf,.txt,.md" className="hidden" disabled={busy} onChange={(e) => { up(e.target.files?.[0]); e.target.value = ""; }} data-testid="kb-upload-input" />
      </label>
    </div>);
}

export default function ReceptionistKnowledge() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["rcp-knowledge"], queryFn: async () => (await api.get("/receptionist/knowledge")).data });
  const { data: branches = [] } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data, staleTime: 300e3 });
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const reload = () => qc.invalidateQueries({ queryKey: ["rcp-knowledge"] });
  const save = async () => {
    setBusy(true);
    try {
      if (f.id) await api.put(`/receptionist/knowledge/${f.id}`, { title: f.title, body: f.body || "", branch_id: f.branch_id || "" });
      else await api.post("/receptionist/knowledge", { kind: f.kind, title: f.title, body: f.body, branch_id: f.branch_id });
      toast.success("Saved — the receptionist uses this right away"); setF(null); reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async (it) => {
    if (!window.confirm(`Delete "${it.title}"? The receptionist will stop using it immediately.`)) return;
    try { await api.delete(`/receptionist/knowledge/${it.id}`); toast.success("Deleted"); reload(); } catch (e) { toast.error(apiErr(e)); }
  };
  if (isLoading) return <Loading />;
  const items = data?.items || [];
  return (
    <div className="mx-auto max-w-4xl space-y-5" data-testid="kb-page">
      <div>
        <Link to="/settings/ai-receptionist" className="text-sm text-violet-700 underline" data-testid="kb-back">AI Receptionist</Link>
        <h1 className="mt-1 text-2xl font-bold">Business knowledge</h1>
        <p className="text-sm text-slate-600">FAQs, policies and documents your AI Receptionist may use to answer callers. Live prices, stock, staff and opening hours always come from your Pawkt records. Never upload customer, pet or medical records here.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => setF({ ...BLANK })} data-testid="kb-add">Add FAQ or policy</Button>
        <UploadBox branches={branches} onDone={reload} />
      </div>
      {f && <Editor f={f} set={setF} branches={branches} onSave={save} onCancel={() => setF(null)} busy={busy} />}
      {items.length ? <ul className="space-y-2" data-testid="kb-list">{items.map((it) => <Row key={it.id} it={it} branches={branches} onEdit={(x) => setF({ ...BLANK, ...x })} onDelete={del} />)}</ul>
        : <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="kb-empty">No knowledge added yet.</p>}
    </div>);
}
